import { useRecoilCallback, useRecoilState, useRecoilValue } from "recoil";
import { AiChatLevelAtom } from "../state/aiChatAtoms";
import { EditorIsClean, HostId, IsUploading } from "../state/atoms";
import { DemoMode, ShowTour } from "../state/uiAtoms";

import { type SDK_Stack, strings } from "@quickpoint.me/shared";
import { useEffect } from "react";
import { useSaveStack } from "../hooks/useSaveStack";
import { saveStack } from "../lib/messageBus";
import { ShowStacks, StackAtomFamily, StackCfKeyAtom } from "../state/stackAtoms";
import { ComposedStack, demoStack } from "../state/stackSelectors";

type BeforeunloadHandler = (evt: BeforeUnloadEvent) => void;

function useSaveOnVisibilityChange({
	editorIsClean,
}: { editorIsClean: boolean; hostId: string }) {
	const save = useSaveStack();

	useEffect(() => {
		const handleVisibilityChange = () => {
			if (!editorIsClean) {
				save();
			}
		};
		window.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			window.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [editorIsClean, save]);
}

function useSaveAfterInactivity({
	editorIsClean,
	hostId,
}: { editorIsClean: boolean; hostId: string }) {
	const save = useRecoilCallback(
		({ snapshot }) =>
			() => {
				const stackToSave = snapshot.getLoadable(ComposedStack).getValue();
				saveStack(hostId, stackToSave as SDK_Stack);
			},
		[hostId],
	);

	useEffect(() => {
		let st: NodeJS.Timeout;
		if (!editorIsClean) {
			st = setTimeout(
				() => {
					save();
				},
				5 * 60 * 1000,
			);
		}

		return () => {
			clearTimeout(st);
		};
	}, [editorIsClean, save]);
}

function useBeforeunload({
	editorIsClean,
	hostId,
}: { editorIsClean: boolean; hostId: string }) {
	const [_isUploading, setIsUploading] = useRecoilState(IsUploading);
	const save = useRecoilCallback(
		({ snapshot }) =>
			() => {
				const stackToSave = snapshot.getLoadable(ComposedStack).getValue();
				saveStack(hostId, stackToSave as SDK_Stack);
			},
		[hostId],
	);

	useEffect(() => {
		const handleBeforeunload: BeforeunloadHandler = (evt) => {
			if (!editorIsClean) {
				save();
				setIsUploading(true);
				evt.preventDefault();
				// Included for legacy support, e.g. Chrome/Edge < 119
				evt.returnValue = true;
			}
		};

		window.addEventListener("beforeunload", handleBeforeunload);
		return () => {
			window.removeEventListener("beforeunload", handleBeforeunload);
		};
	}, [editorIsClean, setIsUploading, save]);
}

function BabySitter() {
	const editorIsClean = useRecoilValue(EditorIsClean);
	const hostId = useRecoilValue(HostId);
	// const devEnv = process.env.NODE_ENV === "development";
	const demoMode = useRecoilCallback(
		({ set }) =>
			() => {
				// Prime StackAtomFamily so the normal Recoil path works in demo mode.
				// Must happen before DemoMode is set so StackSelectorFamily finds a
				// synchronous value and never triggers the async getStackFromBackEnd default.
				// Works in both orientations — portrait runs the mobile tour.
				set(StackCfKeyAtom, strings.demo.cfKey);
				set(StackAtomFamily(strings.demo.cfKey), demoStack);
				set(DemoMode, true);
				set(AiChatLevelAtom, 1);
				// the presentations popup would cover the tour
				set(ShowStacks, false);
				set(ShowTour, true);
			},
		[],
	);

	//create an effect that checks the URL for a param enterDemo that if set to ture triggers the demoMode callback
	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.get("enterDemo") === "true") {
			demoMode();
		}
	}, [demoMode]);

	useSaveOnVisibilityChange({ editorIsClean, hostId });
	useSaveAfterInactivity({ editorIsClean, hostId });
	useBeforeunload({ editorIsClean, hostId });
	return <></>;
}

export default BabySitter;
